import { Suspense, useState } from 'react';
import { Context as ResponsiveContext } from 'react-responsive';
import { hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider as ReakitProvider } from 'reakit';

import { I18nProvider } from '@cx-modules/i18n-provider';
import { DefaultLoadingFallback } from '@cx-modules/react-utils';

import { ErrorBoundary } from './components/ErrorBoundary';
import { MatchesCouponProvider } from './context';
import { MatchesCoupon } from './MatchesCoupon';
import { type MatchesCouponIndexProps } from './types';
import { breakpoints } from './utils';

const createQueryClient = (nocache?: boolean) =>
  new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: nocache ? 0 : undefined,
        refetchOnMount: nocache ? true : false,
        retry: (failureCount, error) => {
          // Don't retry on 4xx errors (client errors like 401, 404)
          if (error instanceof Error && 'status' in error) {
            const status = (error as any).status;
            if (status >= 400 && status < 500) return false;
          }
          // Retry up to 2 times for other errors (network, 5xx)
          return failureCount < 2;
        },
        staleTime: nocache ? 0 : Infinity,
      },
    },
  });

function MatchesCouponWithQuery({ dehydratedState, deviceType, nocache, ...props }: MatchesCouponIndexProps) {
  const [queryClient] = useState(() => {
    const client = createQueryClient(nocache);
    hydrate(client, dehydratedState);
    return client;
  });

  const isoRender = (
    <Suspense fallback={<DefaultLoadingFallback />}>
      <ReakitProvider>
        <QueryClientProvider client={queryClient}>
          <MatchesCouponProvider {...props}>
            <I18nProvider i18nInstance={props.i18n}>
              <ErrorBoundary>
                <MatchesCoupon {...props} />
              </ErrorBoundary>
            </I18nProvider>
          </MatchesCouponProvider>
        </QueryClientProvider>
      </ReakitProvider>
    </Suspense>
  );

  if (typeof window !== 'undefined') {
    return isoRender;
  }

  const deviceWidth =
    deviceType === 'MOBILE' ? breakpoints.phoneSm : deviceType === 'TABLET' ? breakpoints.tablet : breakpoints.desktop;

  return <ResponsiveContext.Provider value={{ width: deviceWidth }}>{isoRender}</ResponsiveContext.Provider>;
}

export { MatchesCouponWithQuery };

// Server-only exports moved to ./server.ts to avoid client bundling
export type { MatchesCouponIndexProps } from './types';
